import React, { useCallback, useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext';
import { getHomePageConfig } from 'src/modules/home/HomePageMapping';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic';
import {
    MESSAGES,
    MandatoryAttributes, OperationMode,
} from 'src/modules/common/constants';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MainLayout } from 'src/modules/common/layouts/MainLayout';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton';
import { HomeFilters } from 'src/modules/home/components/HomeFilters';
import { HomeValves } from 'src/modules/home/components/HomeValves';
import { RuntimeContext } from 'src/runtimeContext';
import { useGetHomeData } from 'src/modules/home/useGetHomeData';
import { ErrorModal } from 'src/modules/common/components/ErrorModal/ErrorModal.tsx';
import { NoData } from 'src/modules/common/components/NoData/NoData.tsx';
import { SkeletonMainLayout } from 'src/modules/common/skeletons/SkeletonMainLayout/SkeletonMainLayout.tsx';
import { Button } from 'src/modules/common/components/Button/Button';
import { ResetIcon } from 'src/modules/common/icons/ResetIcon';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { createUpdateUrlAndState } from 'src/modules/home/utils/createUpdateUrlAndState';
import { isPressureDependentSelected } from 'src/modules/home/isPressureDependentSelected.ts';
import { RegionsEnum } from 'src/generated/operations';
import { useLocationState } from 'src/modules/user-input/useLocationState';
import { getInitialState, initInputState } from 'src/modules/user-input/InitInputState';
import { targetUnits, targetUnitsAm } from 'src/modules/user-input/targetUnits';
import { buildReadableFilterStatus } from 'src/modules/home/buildReadableFilterStatus';
import { ValveBreadcrumbs } from 'src/modules/common/components/ValveBreadcrumbs/ValveBreadcrumbs';
import { PATHS } from 'src/types/Paths';
import { SelectionsButton } from 'src/projects/components/SelectionsButton.tsx';

export const HomePage = (): React.ReactNode => {
    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);
    const [locationState] = useLocationState();

    const pageConfig = useMemo(() => getHomePageConfig(userSettings.currentRegion), [userSettings]);
    const [
        searchParams,
        filter,
        checkboxHandler,,
        resetHandler,
        setSearchParams,
    ] = useFilterLogic(pageConfig.filterConfig);

    const userInputInitialState = getInitialState(userSettings.currentRegion === RegionsEnum.Am
        ? targetUnitsAm
        : targetUnits);

    const userInputState = useMemo(() => initInputState(
        searchParams,
        userInputInitialState,
    ), [userInputInitialState, searchParams]);

    const updateUrlAndState = useMemo(
        () => createUpdateUrlAndState(setSearchParams, searchParams),
        [setSearchParams, searchParams],
    );

    const onChangeRadioHandler = useCallback((detail: { name: string; value: string }) => {
        updateUrlAndState(checkboxHandler(detail));
    }, [checkboxHandler, updateUrlAndState]);

    const onChangeCheckboxHandler = useCallback((detail: { name: string; value: string | string[] }) => {
        updateUrlAndState(checkboxHandler(detail));
    }, [checkboxHandler, updateUrlAndState]);

    const onResetHandler = useCallback(() => {
        resetHandler(pageConfig.filterConfig);
        searchParams.delete('input');
        searchParams.delete('kind');
        updateUrlAndState({});
    }, [searchParams, resetHandler, updateUrlAndState, pageConfig]);

    const state = useGetHomeData({
        query: pageConfig.query,
        filterConfig: pageConfig.filterConfig,
        filter,
        mandatoryFilterConfig,
        currentLocale: userSettings.currentLocale,
        currentRegion: userSettings.currentRegion,
    });

    const notEmpty = Object.keys(filter || {}).length > 0;

    const isMandatorySelected = useMemo(() => {
        const keys = Object.keys(state.filter || {});
        const mandatoryAttributes = [
            ...MandatoryAttributes,
            ...(isPressureDependentSelected(state.filter) ? [OperationMode] : []),
        ];
        return mandatoryAttributes.filter((item) => keys.includes(item)).length === mandatoryAttributes.length;
    }, [state]);

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <Button
                kind="tertiary"
                onClick={onResetHandler}
                fullWidth={isMobile}
            >
                <I18n messageId={MESSAGES.Reset}/>

                <div className="vss-home-page__button-icon">
                    <ResetIcon/>
                </div>
            </Button>

            <NavigationButton
                eventData={{
                    event: 'gaEvent',
                    eventCategory: 'sizing tool',
                    eventAction: 'step 1 - application parameters',
                    eventLabel: searchParams.get('kind') ? 'refresh results' : 'search',
                }}
                type="forward"
                disabled={!isMandatorySelected}
                fullWidth={isMobile}
                state={locationState}
                to={{
                    pathname: PATHS.UserInput,
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    if (state.error) {
        return <ErrorModal/>;
    }

    if (state.data === undefined) {
        return <SkeletonMainLayout/>;
    }

    return (
        <>
            <MainLayout isLoading={state.isLoading}>
                <MainLayoutHeader
                    titleId={MESSAGES.homePageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={(
                        <ValveBreadcrumbs
                            currentPage={PATHS.Home}
                            homeValue={buildReadableFilterStatus(state.filter, state.data)}
                            userInputState={userInputState}
                            isHomePageMandatoryMissing={!isMandatorySelected}
                            locationState={locationState}
                            searchParams={searchParams}
                            resetHandler={notEmpty ? onResetHandler : undefined}
                        />
                    )}
                    selectionButton={(
                        <SelectionsButton searchParams={searchParams} locationState={locationState}/>
                    )}
                />

                {state.data ? (
                    <div className="vss-home-page">
                        <HomeFilters
                            filterConfig={pageConfig.filterConfig}
                            onChangeHandler={onChangeRadioHandler}
                            homePageData={state.data}
                            filter={state.filter}
                            userFilter={filter}
                        />

                        <div className="vss-home-page__devices-container">
                            <h2 className="vss-home-page__devices-title">
                                <I18n messageId={MESSAGES.homePageValvesTitle}/>
                            </h2>

                            <div className="vss-home-valves">
                                <HomeValves
                                    region={userSettings.currentRegion}
                                    homePageData={state.data}
                                    filter={state.filter}
                                    userFilter={filter}
                                    checkboxHandler={onChangeCheckboxHandler}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <NoData/>
                )}
            </MainLayout>

            {isMobile && navigation}
        </>
    );
};
